// ═══════════════════════════════════════════════════════════════
// SCHOLARSHIPS TAB — CRUD + Donor Linking + Award History
// Path: src/components/scholarships/ScholarshipsTab.jsx
// ═══════════════════════════════════════════════════════════════

import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Plus,
  Edit3,
  Save,
  X,
  Trash2,
  UserPlus,
  Search,
  ChevronLeft,
  GraduationCap,
  Award,
  Users,
  DollarSign,
} from "lucide-react";
import { useApp } from "../../context/AppContext";
import { FONT_SIZES, FONT_FAMILY, ICON_SIZES } from "../../constants/appConfig";
import { ActionButton } from "../ui/ActionButton";
import {
  scholarshipsService,
  scholarshipDonorsService,
  scholarshipAwardsService,
} from "../../services/scholarshipService";

const fmtCurrency = (amount) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
  }).format(amount || 0);

export const ScholarshipsTab = () => {
  const { palette, members, showMessage, showLoading, hideLoading, navigate } = useApp();

  const [scholarships, setScholarships] = useState([]);
  const [selected, setSelected] = useState(null);
  const [donors, setDonors] = useState([]);
  const [awards, setAwards] = useState([]);
  const [mode, setMode] = useState("list"); // list | detail | create
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ name: "", description: "" });
  const [donorSearch, setDonorSearch] = useState("");
  const [showDonorPicker, setShowDonorPicker] = useState(false);

  // ── Load ────────────────────────────────────────────────────
  const loadScholarships = useCallback(async () => {
    showLoading("Loading scholarships...");
    try {
      const data = await scholarshipsService.getAll();
      setScholarships(data);
    } catch (err) {
      // silent
    } finally {
      hideLoading();
    }
  }, [showLoading, hideLoading]);

  useEffect(() => {
    loadScholarships();
  }, [loadScholarships]);

  const loadDetail = useCallback(async (scholarship) => {
    setSelected(scholarship);
    setMode("detail");
    setForm({ name: scholarship.name, description: scholarship.description || "" });
    showLoading("Loading scholarship details...");
    try {
      const [donorData, awardData] = await Promise.all([
        scholarshipDonorsService.getByScholarship(scholarship.id),
        scholarshipAwardsService.getByScholarship(scholarship.id),
      ]);
      setDonors(donorData);
      setAwards(awardData);
    } catch (err) {
      // silent
    } finally {
      hideLoading();
    }
  }, [showLoading, hideLoading]);

  // ── CRUD ────────────────────────────────────────────────────
  const handleCreate = async () => {
    if (!form.name.trim()) return;
    try {
      await scholarshipsService.create(form);
      setForm({ name: "", description: "" });
      setMode("list");
      loadScholarships();
      showMessage("Scholarship created");
    } catch (err) {
      showMessage("Error creating scholarship", "error");
    }
  };

  const handleSave = async () => {
    if (!selected) return;
    try {
      const updated = await scholarshipsService.update(selected.id, form);
      setSelected(updated);
      setEditing(false);
      loadScholarships();
      showMessage("Scholarship updated");
    } catch (err) {
      showMessage("Error saving", "error");
    }
  };

  const handleDelete = async () => {
    if (!selected) return;
    try {
      await scholarshipsService.remove(selected.id);
      setMode("list");
      loadScholarships();
      showMessage("Scholarship deleted", "warning");
    } catch (err) {
      showMessage("Error deleting", "error");
    }
  };

  // ── Donor linking ───────────────────────────────────────────
  const donorSearchResults = useMemo(() => {
    if (!donorSearch.trim() || donorSearch.length < 2) return [];
    const q = donorSearch.toLowerCase();
    const linkedIds = new Set(donors.map((d) => d.member_id));
    return members
      .filter((m) => {
        if (linkedIds.has(m.id)) return false;
        const name = `${m.firstName || ""} ${m.lastName || ""}`.toLowerCase();
        const org = (m.organization || "").toLowerCase();
        return name.includes(q) || org.includes(q);
      })
      .slice(0, 8);
  }, [donorSearch, members, donors]);

  const handleLinkDonor = async (memberId) => {
    if (!selected) return;
    try {
      await scholarshipDonorsService.link(selected.id, memberId);
      const refreshed = await scholarshipDonorsService.getByScholarship(selected.id);
      setDonors(refreshed);
      setDonorSearch("");
      setShowDonorPicker(false);
      showMessage("Donor linked");
    } catch (err) {
      showMessage("Error linking donor", "error");
    }
  };

  const handleUnlinkDonor = async (linkId) => {
    try {
      await scholarshipDonorsService.unlink(linkId);
      setDonors((prev) => prev.filter((d) => d.id !== linkId));
      showMessage("Donor unlinked", "warning");
    } catch (err) {
      showMessage("Error unlinking", "error");
    }
  };

  const getDonorName = (rec) => {
    if (!rec.members) return "—";
    return (
      rec.members.credentialed_full_name ||
      `${rec.members.first_name || ""} ${rec.members.last_name || ""}`.trim() ||
      "—"
    );
  };

  // ── Render ──────────────────────────────────────────────────

  // CREATE MODE
  if (mode === "create") {
    return (
      <div
        style={{
          background: palette.cardBg,
          border: `1px solid ${palette.cardBorder}`,
          borderRadius: 10,
          padding: 24,
          maxWidth: 500,
        }}
      >
        <div style={{ fontSize: FONT_SIZES.sm, fontWeight: 700, color: palette.text1, fontFamily: FONT_FAMILY, marginBottom: 16 }}>
          New Scholarship
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <div>
            <label style={{ fontSize: "0.6rem", fontWeight: 600, color: palette.text2, fontFamily: FONT_FAMILY, display: "block", marginBottom: 4 }}>
              Scholarship Name *
            </label>
            <input
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              placeholder="e.g., The Smith Family Scholarship"
              style={{
                width: "100%",
                padding: "8px 12px",
                borderRadius: 8,
                fontSize: FONT_SIZES.xs,
                fontFamily: FONT_FAMILY,
                outline: "none",
                background: palette.inputBg,
                color: palette.inputColor,
                border: `1px solid ${palette.inputBorder}`,
              }}
            />
          </div>
          <div>
            <label style={{ fontSize: "0.6rem", fontWeight: 600, color: palette.text2, fontFamily: FONT_FAMILY, display: "block", marginBottom: 4 }}>
              Description
            </label>
            <textarea
              value={form.description}
              onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
              rows={3}
              style={{
                width: "100%",
                padding: "8px 12px",
                borderRadius: 8,
                fontSize: FONT_SIZES.xs,
                fontFamily: FONT_FAMILY,
                outline: "none",
                background: palette.inputBg,
                color: palette.inputColor,
                border: `1px solid ${palette.inputBorder}`,
                resize: "vertical",
              }}
            />
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <ActionButton label="Cancel" variant="secondary" small onClick={() => { setMode("list"); setForm({ name: "", description: "" }); }} />
            <ActionButton label="Create" icon={Plus} small onClick={handleCreate} disabled={!form.name.trim()} />
          </div>
        </div>
      </div>
    );
  }

  // DETAIL MODE
  if (mode === "detail" && selected) {
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        {/* Back + Actions */}
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <button
            onClick={() => { setMode("list"); setEditing(false); }}
            style={{ display: "flex", alignItems: "center", gap: 4, background: "none", border: "none", cursor: "pointer", color: palette.text2, fontSize: FONT_SIZES.xs, fontWeight: 600, fontFamily: FONT_FAMILY }}
          >
            <ChevronLeft size={ICON_SIZES.sm} /> Back to list
          </button>
          <div style={{ flex: 1 }} />
          {editing ? (
            <>
              <ActionButton label="Cancel" variant="secondary" small onClick={() => setEditing(false)} />
              <ActionButton label="Save" icon={Save} small onClick={handleSave} />
            </>
          ) : (
            <>
              <ActionButton label="Edit" icon={Edit3} variant="secondary" small onClick={() => setEditing(true)} />
              <ActionButton label="Delete" icon={Trash2} variant="danger" small onClick={handleDelete} />
            </>
          )}
        </div>

        {/* Info card */}
        <div
          style={{
            background: palette.cardBg,
            border: `1px solid ${palette.cardBorder}`,
            borderRadius: 10,
            padding: "16px 20px",
          }}
        >
          {editing ? (
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              <input
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                style={{
                  fontSize: FONT_SIZES.md, fontWeight: 700, fontFamily: FONT_FAMILY,
                  padding: "6px 10px", borderRadius: 6,
                  border: `1px solid ${palette.inputBorder}`, background: palette.inputBg, color: palette.inputColor, outline: "none",
                }}
              />
              <textarea
                value={form.description}
                onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                rows={2}
                style={{
                  fontSize: FONT_SIZES.xs, fontFamily: FONT_FAMILY,
                  padding: "6px 10px", borderRadius: 6,
                  border: `1px solid ${palette.inputBorder}`, background: palette.inputBg, color: palette.inputColor, outline: "none", resize: "vertical",
                }}
              />
            </div>
          ) : (
            <>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <GraduationCap size={ICON_SIZES.md} style={{ color: palette.accent1 || "#3b82f6" }} />
                <span style={{ fontSize: FONT_SIZES.md, fontWeight: 700, color: palette.text1, fontFamily: FONT_FAMILY }}>
                  {selected.name}
                </span>
                {!selected.is_active && (
                  <span style={{ fontSize: "0.55rem", fontWeight: 600, padding: "2px 8px", borderRadius: 6, background: "#6b728015", color: "#6b7280", fontFamily: FONT_FAMILY }}>
                    inactive
                  </span>
                )}
              </div>
              {selected.description && (
                <div style={{ fontSize: FONT_SIZES.xs, color: palette.text3, fontFamily: FONT_FAMILY, marginTop: 6 }}>
                  {selected.description}
                </div>
              )}
            </>
          )}
        </div>

        {/* Linked Donors */}
        <div
          style={{
            background: palette.cardBg,
            border: `1px solid ${palette.cardBorder}`,
            borderRadius: 10,
            overflow: "hidden",
          }}
        >
          <div style={{ padding: "12px 16px", borderBottom: `1px solid ${palette.cardBorder}`, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <Users size={ICON_SIZES.sm} style={{ color: palette.text2 }} />
              <span style={{ fontSize: FONT_SIZES.xs, fontWeight: 700, color: palette.text1, fontFamily: FONT_FAMILY }}>
                Donors ({donors.length})
              </span>
            </div>
            <ActionButton label="Link Donor" icon={UserPlus} small onClick={() => setShowDonorPicker(true)} />
          </div>

          {/* Donor picker */}
          {showDonorPicker && (
            <div style={{ padding: "10px 16px", borderBottom: `1px solid ${palette.cardBorder}`, background: `${palette.accent1 || "#3b82f6"}06` }}>
              <div style={{ position: "relative" }}>
                <Search size={ICON_SIZES.sm} style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", color: palette.text3 }} />
                <input
                  value={donorSearch}
                  onChange={(e) => setDonorSearch(e.target.value)}
                  placeholder="Search donors to link..."
                  autoFocus
                  style={{
                    width: "100%", padding: "7px 10px 7px 32px", borderRadius: 6,
                    fontSize: FONT_SIZES.xs, fontFamily: FONT_FAMILY, outline: "none",
                    background: palette.inputBg, color: palette.inputColor, border: `1px solid ${palette.inputBorder}`,
                  }}
                />
              </div>
              {donorSearchResults.length > 0 && (
                <div style={{ marginTop: 6, maxHeight: 160, overflowY: "auto" }}>
                  {donorSearchResults.map((m) => (
                    <div
                      key={m.id}
                      onClick={() => handleLinkDonor(m.id)}
                      style={{
                        padding: "6px 8px", borderRadius: 4, cursor: "pointer",
                        fontSize: FONT_SIZES.xs, color: palette.text1, fontFamily: FONT_FAMILY,
                        transition: "background 0.1s",
                      }}
                      onMouseEnter={(e) => { e.currentTarget.style.background = `${palette.accent1}10`; }}
                      onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
                    >
                      {m.credentialedFullName || `${m.firstName || ""} ${m.lastName || ""}`.trim() || "Unknown"}
                      {m.organization && <span style={{ color: palette.text3, marginLeft: 6 }}>({m.organization})</span>}
                    </div>
                  ))}
                </div>
              )}
              <button
                onClick={() => { setShowDonorPicker(false); setDonorSearch(""); }}
                style={{ marginTop: 6, fontSize: "0.6rem", color: palette.text3, background: "none", border: "none", cursor: "pointer", fontFamily: FONT_FAMILY }}
              >
                Cancel
              </button>
            </div>
          )}

          {/* Donor list */}
          {donors.length === 0 ? (
            <div style={{ padding: 20, textAlign: "center", fontSize: FONT_SIZES.xs, color: palette.text3, fontFamily: FONT_FAMILY }}>
              No donors linked yet.
            </div>
          ) : (
            donors.map((d) => (
              <div
                key={d.id}
                style={{
                  display: "flex", alignItems: "center", gap: 8,
                  padding: "8px 16px", borderBottom: `1px solid ${palette.cardBorder}`,
                  cursor: "pointer", transition: "background 0.1s",
                }}
                onMouseEnter={(e) => { e.currentTarget.style.background = `${palette.accent1}06`; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
              >
                <div
                  style={{ flex: 1, minWidth: 0 }}
                  onClick={() => {
                    const member = members.find((m) => m.id === d.member_id);
                    if (member) navigate("memberDetail", member);
                  }}
                >
                  <div style={{ fontSize: FONT_SIZES.xs, fontWeight: 600, color: palette.text1, fontFamily: FONT_FAMILY }}>
                    {getDonorName(d)}
                  </div>
                  {d.since_year && (
                    <div style={{ fontSize: "0.55rem", color: palette.text3, fontFamily: FONT_FAMILY }}>
                      Since {d.since_year}
                    </div>
                  )}
                </div>
                <button
                  onClick={(e) => { e.stopPropagation(); handleUnlinkDonor(d.id); }}
                  style={{ background: "none", border: "none", cursor: "pointer", padding: 2, display: "flex", opacity: 0.4 }}
                  onMouseEnter={(e) => { e.currentTarget.style.opacity = "1"; }}
                  onMouseLeave={(e) => { e.currentTarget.style.opacity = "0.4"; }}
                  title="Unlink donor"
                >
                  <X size={ICON_SIZES.sm} style={{ color: palette.text3 }} />
                </button>
              </div>
            ))
          )}
        </div>

        {/* Awards History */}
        <div
          style={{
            background: palette.cardBg,
            border: `1px solid ${palette.cardBorder}`,
            borderRadius: 10,
            overflow: "hidden",
          }}
        >
          <div style={{ padding: "12px 16px", borderBottom: `1px solid ${palette.cardBorder}`, display: "flex", alignItems: "center", gap: 6 }}>
            <Award size={ICON_SIZES.sm} style={{ color: palette.text2 }} />
            <span style={{ fontSize: FONT_SIZES.xs, fontWeight: 700, color: palette.text1, fontFamily: FONT_FAMILY }}>
              Awards History ({awards.length})
            </span>
          </div>
          {awards.length === 0 ? (
            <div style={{ padding: 20, textAlign: "center", fontSize: FONT_SIZES.xs, color: palette.text3, fontFamily: FONT_FAMILY }}>
              No awards recorded yet. Use the Awards tab to add disbursements.
            </div>
          ) : (
            awards.map((a) => (
              <div
                key={a.id}
                style={{
                  display: "flex", alignItems: "center", gap: 10,
                  padding: "8px 16px", borderBottom: `1px solid ${palette.cardBorder}`,
                }}
              >
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: FONT_SIZES.xs, fontWeight: 600, color: palette.text1, fontFamily: FONT_FAMILY }}>
                    {a.student_name}
                  </div>
                  <div style={{ fontSize: "0.55rem", color: palette.text3, fontFamily: FONT_FAMILY }}>
                    {a.season}{a.award_date ? ` · ${new Date(a.award_date + "T12:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}` : ""}
                  </div>
                </div>
                <span style={{ fontSize: FONT_SIZES.xs, fontWeight: 600, color: palette.accent1 || "#3b82f6", fontFamily: FONT_FAMILY }}>
                  {fmtCurrency(a.award_amount)}
                </span>
              </div>
            ))
          )}
        </div>
      </div>
    );
  }

  // LIST MODE
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <div style={{ display: "flex", justifyContent: "flex-end" }}>
        <ActionButton label="New Scholarship" icon={Plus} small onClick={() => { setMode("create"); setForm({ name: "", description: "" }); }} />
      </div>

      {scholarships.length === 0 ? (
        <div style={{ textAlign: "center", padding: 40, color: palette.text3, fontSize: FONT_SIZES.xs, fontFamily: FONT_FAMILY }}>
          No scholarships yet. Click "New Scholarship" to get started.
        </div>
      ) : (
        scholarships.map((s) => (
          <div
            key={s.id}
            onClick={() => loadDetail(s)}
            style={{
              background: palette.cardBg,
              border: `1px solid ${palette.cardBorder}`,
              borderRadius: 10,
              padding: "14px 18px",
              cursor: "pointer",
              transition: "background 0.1s",
              display: "flex",
              alignItems: "center",
              gap: 14,
              opacity: s.is_active ? 1 : 0.5,
            }}
            onMouseEnter={(e) => { e.currentTarget.style.background = `${palette.accent1}06`; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = palette.cardBg; }}
          >
            <div
              style={{
                width: 40, height: 40, borderRadius: 10,
                background: `${palette.accent1 || "#3b82f6"}15`,
                display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
              }}
            >
              <GraduationCap size={ICON_SIZES.md} style={{ color: palette.accent1 || "#3b82f6" }} />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: FONT_SIZES.sm, fontWeight: 700, color: palette.text1, fontFamily: FONT_FAMILY }}>
                {s.name}
              </div>
              {s.description && (
                <div style={{ fontSize: "0.6rem", color: palette.text3, fontFamily: FONT_FAMILY, marginTop: 2, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                  {s.description}
                </div>
              )}
            </div>
            {!s.is_active && (
              <span style={{ fontSize: "0.55rem", fontWeight: 600, padding: "2px 8px", borderRadius: 6, background: "#6b728015", color: "#6b7280", fontFamily: FONT_FAMILY }}>
                inactive
              </span>
            )}
          </div>
        ))
      )}
    </div>
  );
};
